<script setup>
/*
    WidgetProperties - Widget properties 
 */
import {onBeforeMount, reactive, watch} from 'vue'
import {State, clone, titlecase} from '@/paks/vu-app'
import {cleanWidget} from './BoardUtils'
import Json5 from 'json5'

const props = defineProps({
    board: Object,
    modelValue: [Object],
})

const page = reactive({
    dashboard: props.board,
    properties: [],
    rules: [],
    select: {},
    showColors: {},
    tableFields: null,
    types: null,
    widget: {},
})

const WidgetTypes = [
    {value: 'audio', title: 'Audio'},
    {value: 'event', title: 'Event'},
    {value: 'gauge', title: 'Gauge'},
    {value: 'graph', title: 'Graph'},
    {value: 'image', title: 'Image'},
    {value: 'input', title: 'Input'},
    {value: 'info', title: 'Info'},
    {value: 'label', title: 'Label'},
    {value: 'line', title: 'Line'},
    {value: 'led', title: 'Led'},
    {value: 'list', title: 'List'},
    {value: 'metric', title: 'Metric Table'},
    {value: 'numeric', title: 'Numeric'},
    {value: 'progress', title: 'Progress'},
    // {value: 'set', title: 'Service Overview'},
    {value: 'shape', title: 'Shape'},
    // {value: 'sign', title: 'Sign'},
    {value: 'speedometer', title: 'Speedometer'},
    {value: 'table', title: 'Table'},
    {value: 'tabs', title: 'Tabs'},
    {value: 'toolbar', title: 'Toolbar'},
    {value: 'video', title: 'Video'},
]

const InputTypes = [
    {value: 'button', title: 'Button'},
    {value: 'checkbox', title: 'Checkbox'},
    {value: 'combo', title: 'Combination Box'},
    {value: 'date', title: 'Date'},
    {value: 'file', title: 'File'},
    {value: 'form', title: 'Form'},
    {value: 'icon', title: 'Icon'},
    {value: 'password', title: 'Password'},
    {value: 'radio', title: 'Radio'},
    {value: 'select', title: 'Select'},
    {value: 'slider', title: 'Slider'},
    {value: 'switch', title: 'Switch'},
    {value: 'text', title: 'Text Field'},
    {value: 'textarea', title: 'Text Area'},
]

const Fields = [
    {name: 'accept', hint: 'Acceptable upload file types'},
    {name: 'columns', hint: 'Table columns definition (JSON)'},
    {name: 'datetime', hint: 'Date or time'},
    {name: 'field', hint: 'Database item field name'},
    {name: 'fields', hint: 'Database fields to fetch'},
    {name: 'footer', hint: 'Footer at the bottom of the widget'},
    {name: 'form', hint: 'Input form name'},
    {name: 'formField', hint: 'Input data form field name', label: 'Form Field'},
    {name: 'format', hint: 'Number format: +0#,###.#^'},
    {name: 'header', hint: 'Widget body header'},
    {name: 'icon', hint: 'Select icon', type: 'select'},
    {name: 'items', hint: 'Select items'},
    {name: 'label', hint: 'Input field label'},
    {name: 'max', hint: 'Maximum value'},
    {name: 'min', hint: 'Minimum value'},
    {name: 'multiple', hint: 'Multiple selection'},
    {name: 'pageSize', hint: 'Initial page size'},
    {name: 'pivot', hint: 'Enter pivot column'},
    {name: 'placeholder', hint: 'Regular expression to match (without slashes)'},
    {name: 'prefix', hint: 'Prefix to display before widget value'},
    {name: 'presentation', hint: 'Graph type'},
    {name: 'rows', hint: 'Number of text area rows'},
    {name: 'subtitle', hint: 'Table subtitle'},
    {name: 'suffix', hint: 'Suffix to display after widget value'},
    {name: 'text', hint: 'Static widget value'},
    {name: 'timezone', hint: 'Date input timezone'},
    {name: 'url', hint: 'Image URL, or map of URLs'},
    {name: 'validate', hint: 'Regular expression to validate input'},
]

const Select = {
    audio: {
        footer: 'text',
        header: 'text',
        text: 'text',
        url: 'textarea',
    },
    button: {
        footer: 'text',
        header: 'text',
        prefix: 'text',
        suffix: 'text',
        text: 'text',
    },
    checkbox: {
        form: 'text',
        formField: 'text',
        label: 'text',
        type: 'text',
    },
    combo: {
        form: 'text',
        formField: 'text',
        items: 'text',
        label: 'text',
        multiple: 'switch',
        type: 'text',
        validate: 'text',
    },
    date: {
        datetime: ['date', 'time', 'datetime'],
        form: 'text',
        formField: 'text',
        label: 'text',
        prefix: 'text',
        suffix: 'text',
        timezone: 'text',
        type: 'text',
    },
    event: {
        max: 'text',
    },
    file: {
        accept: 'text',
        multiple: 'switch',
        form: 'text',
        formField: 'text',
        label: 'text',
        type: 'text',
    },
    form: {
        text: 'text',
        form: 'text',
        formField: 'text',
        type: 'text',
    },
    gauge: {
        footer: 'text',
        header: 'text',
        format: 'text',
        min: 'text',
        max: 'text',
    },
    graph: {
        header: 'text',
        footer: 'text',
        min: 'text',
        max: 'text',
        presentation: ['line'],
        /*
        presentation: [
            'bar',
            'line',
            'scatter',
            'bubble',
            'pie',
            'doughnut',
            'polarArea',
            'radar',
        ], */
    },
    icon: {
        text: 'text',
    },
    image: {
        header: 'text',
        url: 'textarea',
    },
    input: {
        form: 'text',
        formField: 'text',
        label: 'text',
        type: 'text',
    },
    info: {
        footer: 'text',
        format: 'text',
        header: 'text',
        icon: 'select',
        prefix: 'text',
        suffix: 'text',
    },
    label: {
        footer: 'text',
        header: 'text',
        prefix: 'text',
        suffix: 'text',
        text: 'text',
    },
    led: {
        header: 'text',
        footer: 'text',
    },
    line: {
        header: 'text',
        footer: 'text',
    },
    list: {
        header: 'text',
        footer: 'text',
        subtitle: 'text',
    },
    metric: {
        fields: 'text',
        header: 'text',
        pageSize: 'text',
        pivot: 'text',
        subtitle: 'text',
    },
    numeric: {
        footer: 'text',
        format: 'text',
        header: 'text',
        prefix: 'text',
        suffix: 'text',
    },
    password: {
        form: 'text',
        formField: 'text',
        label: 'text',
        prefix: 'text',
        suffix: 'text',
        type: 'text',
    },
    progress: {
        header: 'text',
        footer: 'text',
        min: 'text',
        max: 'text',
    },
    radio: {
        items: 'text',
        form: 'text',
        formField: 'text',
        label: 'text',
        type: 'text',
    },
    select: {
        items: 'text',
        form: 'text',
        formField: 'text',
        label: 'text',
        multiple: 'switch',
        type: 'text',
    },
    set: {},
    shape: {
        // type: ['circle', 'square', 'triangle', 'diamond', 'pentagon', 'hexagon', 'octagon', 'star', 'heart', 'cloud', 'star-filled', 'heart-filled', 'cloud-filled']
    },
    /*
    sign: {
        header: 'text',
    }, */
    slider: {
        form: 'text',
        formField: 'text',
        label: 'text',
        max: 'text',
        min: 'text',
        prefix: 'text',
        suffix: 'text',
        type: 'text',
    },
    speedometer: {
        footer: 'text',
        header: 'text',
        format: 'text',
        min: 'text',
        max: 'text',
    },
    switch: {
        form: 'text',
        formField: 'text',
        label: 'text',
        type: 'text',
    },
    table: {
        columns: 'textarea',
        header: 'text',
        pivot: 'text',
        pageSize: 'text',
        subtitle: 'text',
    },
    tabs: {
        header: 'text',
        items: 'text',
    },
    text: {
        form: 'text',
        formField: 'text',
        label: 'text',
        placeholder: 'text',
        prefix: 'text',
        suffix: 'text',
        type: 'text',
        validate: 'text',
    },
    textarea: {
        form: 'text',
        formField: 'text',
        label: 'text',
        rows: 'text',
        type: 'text',
        validate: 'text',
    },
    toolbar: {},
    video: {
        footer: 'text',
        header: 'text',
        url: 'textarea',
    },
}

const emit = defineEmits(['update:modelValue'])

watch(() => page.widget?.namespace, prepFields)
watch(() => page.widget?.input, prepFields)

onBeforeMount(() => {
    let widget = (page.widget = clone(props.modelValue))
    if (widget.columns) {
        widget.columns = Json5.stringify(widget.columns, null, 4)
    }
    let types = WidgetTypes.filter((t) => !t.beta || State.auth.beta)
    page.types = types.sort(sortByLabel)
    if (!page.widget.id) {
        changeType()
    }
    prepFields()
})

function changeType(v) {
    let widget = page.widget
    widget.max = widget.min = null
    widget.prefix = widget.suffix = null
    if (widget.type == 'shape' && !widget.id && widget.css.length == 0) {
        //  Create a default box
        widget.css.push({property: 'border', value: 'solid 1px black'})
    }
    page.widget = cleanWidget(widget)
    prepFields()
    change()
}

function prepFields() {
    let widget = page.widget
    let select = clone(Select)
    if (props.board.layout == 'exact') {
        select[widget.type].z = true
    }
    for (let field of Fields) {
        if (widget[field] != null) {
            select[widget.type][field] = true
        }
    }
    page.select = select
}

function change() {
    let widget = clone(page.widget)
    if (widget.columns) {
        try {
            widget.columns = Json5.parse(widget.columns)
        } catch (err) {
            widget.columns = widget.columns
                .toString()
                .split(',')
                .map((i) => {
                    return {name: i.trim()}
                })
        }
    }
    emit('update:modelValue', widget)
}

function sortByLabel(a, b) {
    if (a.label < b.label) {
        return -1
    } else if (b.label < a.label) {
        return 1
    }
    return 0
}

function getRules(field) {
    if (field == 'field' || field == 'label') {
        return page.rules.required
    }
    return []
}

/*
    Return the fields required for the input widget type
 */
function getFields() {
    let key = page.widget.type == 'input' ? page.widget.input : page.widget.type
    let select = page.select[key]
    let fields = Fields.filter((f) => select?.[f.name])
    for (let f of fields) {
        if (Array.isArray(select[f.name])) {
            f.items = select[f.name]
            f.type = 'select'
        } else {
            f.type = select[f.name] || 'text'
        }
    }
    fields = fields.filter((f) => f.type != 'select' && f.name != 'icon')
    if (page.widget.input == 'form') {
        fields = fields.filter((f) => f.name != 'formField')
    }
    return fields
}
</script>

<template>
    <v-container fluid class="widget-style pa-0">
        <vu-input
            v-model="page.widget.type"
            label="Widget Type"
            type="select"
            :items="page.types"
            :rules="page.rules.required"
            @update:modelValue="changeType" />

        <vu-input
            v-if="page.widget.type == 'input'"
            v-model="page.widget.input"
            label="Widget Input Type"
            type="select"
            :items="InputTypes"
            @update:modelValue="change" />

        <vu-input
            v-for="field of getFields()"
            v-model="page.widget[field.name]"
            transition="false"
            :clearable="true"
            :key="field.name"
            :name="titlecase(field.name)"
            :label="titlecase(field.label || field.name)"
            :placeholder="field.hint"
            :items="field.items"
            :rows="10"
            :rules="getRules(field.name)"
            :type="field.type"
            @update:modelValue="change" />

        <vu-icon
            v-if="page.select[page.widget?.type].icon"
            v-model="page.widget.icon"
            @update:modelValue="change" />

        <vu-input
            v-if="page.dashboard.framed"
            v-model="page.widget.title"
            label="Widget Frame Title"
            placeholder="Title to display when widgets are framed"
            type="text"
            :clearable="true"
            :placeholder="page.widget.id ? '' : 'Blank for default title'"
            @update:modelValue="change" />
    </v-container>
</template>

<style lang="scss">
.widget-style {
    h2 {
        font-size: 1.4rem;
        font-weight: normal;
    }
    .v-alert {
        width: 100%;
    }
    label {
        color: rgb(var(--v-theme-text));
        font-weight: normal;
        font-size: 1.2rem;
    }
    .color-picker {
        margin: 6px 0 16px 0;
        border: solid 1px rgb(var(--v-theme-border));
        max-width: 100% !important;
        width: 100% !important;
    }
    td.editable {
        padding: 4px 7px 4px 7px;
        background: rgba(130, 178, 255, 0.2) !important;
        margin: 2px;
        cursor: text;
        &:focus {
            outline: 2px solid rgba(0, 0, 255, 0.5) !important;
            background: white !important;
            width: calc(100% - 4px);
        }
    }
    .table-cell {
        padding: 4px !important;
        .cell-attribute {
            padding: 3px;
            background: rgba(50, 50, 50, 0.1);
            color: black;
        }
    }
    .table-cell.table-col-name {
        .cell-value {
            display: inline-block;
            width: 100%;
            margin: 1px;
            padding: 3px;
        }
    }
    .table-cell.table-col-value {
        .cell-value {
            display: inline-block;
            width: calc(100% - 20px);
            margin: 1px;
            padding: 3px;
            padding-right: 0;
        }
    }
    .color-icon {
        margin-top: 2px;
        float: right;
        width: 16px;
    }
    .table-title {
        font-size: 1.6rem !important;
    }
    .add {
        font-size: 12px;
    }
    .divider {
        font-size: 1.2rem !important;
        margin-bottom: 16px !important;
        clear: both;
        width: 100%;
    }
    .v-label {
        font-size: 0.9rem;
    }
}
</style>
